/**
 * Single swappable font configuration for the MyPlayer platform.
 * Changing the default font across the app is a one-line change here,
 * or can be overridden per brand at runtime via `applyBrandThemeOverrides`.
 */
export const FONT_CONFIG = {
    name: 'Montserrat',
    cssFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif",
    googleFontUrl: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap',
    weights: [400, 500, 600, 700],
};
/**
 * Unified dark mode colour tokens aligned across create-myplayer, myprojects-web, and mypack.
 */
export const DARK_DESIGN_COLORS = {
    pageBackground: '#121212',
    surface: '#1C1C1E',
    surfaceFrosted: 'rgba(28, 28, 30, 0.95)',
    surfaceSubtle: '#2C2C2E',
    surfaceMuted: '#242426',
    border: '#2E2E32',
    borderMedium: '#3E3E42',
    borderSubtle: '#252529',
    textPrimary: '#F5F5F7',
    textSecondary: '#A1A1A6',
    textMuted: '#8E8E93',
    brand: '#FF5A1F',
    brandHover: '#E6511C',
    brandActiveWash: 'rgba(255, 90, 31, 0.15)',
    brandHoverWash: 'rgba(255, 90, 31, 0.22)',
    brandBorderTint: 'rgba(255, 90, 31, 0.45)',
    brandContrast: '#FFFFFF',
    statusValid: '#66BB6A',
    statusValidBg: 'rgba(102, 187, 106, 0.18)',
    statusWarning: '#FFA726',
    statusWarningBg: 'rgba(255, 167, 38, 0.18)',
    statusInvalid: '#EF5350',
    statusInvalidBg: 'rgba(239, 83, 80, 0.18)',
    statusRecalled: '#EF5350',
    brandPrimary: '#FF5A1F',
    brandAccent: '#FF5A1F',
};
export const DESIGN_TOKENS = {
    font: FONT_CONFIG,
    colors: {
        pageBackground: '#F4F4F4',
        surface: '#FFFFFF',
        surfaceFrosted: 'rgba(255, 255, 255, 0.95)',
        surfaceSubtle: '#FAF9F8',
        surfaceMuted: '#F4F4F4',
        border: '#E0E0E0',
        borderMedium: '#BDBDBD',
        borderSubtle: '#F0F0F0',
        textPrimary: '#1D1D1F',
        textSecondary: '#6E6E73',
        textMuted: '#8E8E93',
        brand: '#FF5A1F',
        brandHover: '#E6511C',
        brandActiveWash: '#FFF5F2',
        brandHoverWash: '#FFEBE4',
        brandBorderTint: 'rgba(255, 90, 31, 0.35)',
        brandContrast: '#FFFFFF',
        statusValid: '#16A34A',
        statusValidBg: '#DCFCE7',
        statusWarning: '#D97706',
        statusWarningBg: '#FEF3C7',
        statusInvalid: '#DC2626',
        statusInvalidBg: '#FEE2E2',
        statusRecalled: '#991B1B',
        // Aliases for existing callers
        brandPrimary: '#FF5A1F',
        brandAccent: '#FF5A1F',
    },
    darkColors: DARK_DESIGN_COLORS,
    radii: {
        control: '6px',
        card: '8px',
        dock: '12px',
        modal: '12px',
        pill: '9999px',
    },
    shadows: {
        none: 'none',
        cardBase: '0 1px 3px rgba(0, 0, 0, 0.05)',
        hoverLift: '0 4px 12px rgba(0, 0, 0, 0.06)',
        modal: '0 12px 40px rgba(0, 0, 0, 0.12)',
        primaryGlow: '0 4px 14px rgba(255, 90, 31, 0.35)',
    },
    typography: {
        bodySize: '14px',
        labelSize: '12px',
        helperSize: '13px',
        pageTitleSize: '24px',
        pageTitleWeight: 600,
        numericVariant: 'tabular-nums',
    },
    layout: {
        railWidthPx: 68,
        primaryActionSizePx: 52,
        mobileBarHeightPx: 60,
        iconButtonSizePx: 32,
        tableRowHeightPx: 48,
    },
    spacingPx: {
        xs: 4,
        sm: 8,
        md: 16,
        lg: 24,
        xl: 32,
    },
    brandIdentity: {
        logoText: 'MyPlayer',
        logoSubtitle: 'GS1 Digital Link Platform',
        logoUrl: null,
    },
};
export function getValidationBadgeColor(status) {
    switch (status) {
        case 'VALID':
            return DESIGN_TOKENS.colors.statusValid;
        case 'WARNING':
            return DESIGN_TOKENS.colors.statusWarning;
        case 'INVALID':
            return DESIGN_TOKENS.colors.statusInvalid;
        case 'PENDING':
            return DESIGN_TOKENS.colors.brand;
    }
}
/**
 * Applies the active font config and optional white-label brand overrides to
 * the document root CSS variables (`--font-sans`, `--color-brand`, etc.) and
 * injects/updates the Google Font stylesheet link in `document.head`.
 */
export function applyBrandThemeOverrides(overrides) {
    const resolvedFont = {
        ...FONT_CONFIG,
        ...overrides?.font,
    };
    const resolvedColors = {
        ...DESIGN_TOKENS.colors,
        ...overrides?.colors,
    };
    const resolvedIdentity = {
        ...DESIGN_TOKENS.brandIdentity,
        ...overrides?.brandIdentity,
    };
    if (typeof document !== 'undefined') {
        const root = document.documentElement;
        root.style.setProperty('--font-sans', resolvedFont.cssFamily);
        root.style.setProperty('--app-font-family', resolvedFont.cssFamily);
        root.style.setProperty('--color-page-bg', resolvedColors.pageBackground);
        root.style.setProperty('--color-surface', resolvedColors.surface);
        root.style.setProperty('--color-surface-frosted', resolvedColors.surfaceFrosted);
        root.style.setProperty('--color-surface-subtle', resolvedColors.surfaceSubtle);
        root.style.setProperty('--color-surface-muted', resolvedColors.surfaceMuted);
        root.style.setProperty('--color-border', resolvedColors.border);
        root.style.setProperty('--color-border-medium', resolvedColors.borderMedium);
        root.style.setProperty('--color-border-subtle', resolvedColors.borderSubtle);
        root.style.setProperty('--color-text-primary', resolvedColors.textPrimary);
        root.style.setProperty('--color-text-secondary', resolvedColors.textSecondary);
        root.style.setProperty('--color-text-muted', resolvedColors.textMuted);
        root.style.setProperty('--color-brand', resolvedColors.brand);
        root.style.setProperty('--color-brand-hover', resolvedColors.brandHover);
        root.style.setProperty('--color-brand-wash', resolvedColors.brandActiveWash);
        root.style.setProperty('--color-brand-wash-hover', resolvedColors.brandHoverWash);
        root.style.setProperty('--color-brand-border-tint', resolvedColors.brandBorderTint);
        root.style.setProperty('--color-brand-contrast', resolvedColors.brandContrast);
        if (resolvedFont.googleFontUrl && document.head) {
            const linkId = 'mp-brand-font-stylesheet';
            let linkEl = document.getElementById(linkId);
            if (!linkEl) {
                linkEl = document.createElement('link');
                linkEl.id = linkId;
                linkEl.rel = 'stylesheet';
                document.head.appendChild(linkEl);
            }
            if (linkEl.href !== resolvedFont.googleFontUrl) {
                linkEl.href = resolvedFont.googleFontUrl;
            }
        }
    }
    return {
        font: resolvedFont,
        colors: resolvedColors,
        brandIdentity: resolvedIdentity,
    };
}
//# sourceMappingURL=tokens.js.map